import React, { useState, useEffect, useCallback } from 'react';
import { Banner, makeStyles } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { useRobloxAuthentication } from '@rbx/auth';
import creatorSettingsClient from '@modules/clients/creatorSettings';
import throwError from '@modules/utilities/error';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsCoursesEvent from '@modules/eventStream/enum/DocsCoursesEvent';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { coursesPathSuffix } from '@modules/learning/constants/coursesNavConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';

const FALLBACK_LOCAL_STORAGE_KEY = 'creator-doc-site-show-explore-courses-banner';

const ALLOWED_PATHS = new Set(['/avatar', '/get-started', '/tutorials']);

const useStyles = makeStyles()((theme) => ({
  bannerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  oldLayoutRoot: {
    background: 'secondary',
    gap: '16px',
    marginBottom: '40px',
  },
  newLayoutRoot: {
    background: 'secondary',
    gap: '16px',
    width: '100%',
    margin: '16px 32px 32px 32px',
    [theme.breakpoints.down('Large')]: {
      margin: '16px 24px 32px 24px',
    },
  },
}));

interface ExploreCoursesBannerProps {
  enableNewLayout?: boolean;
}

const ExploreCoursesBanner: React.FC<ExploreCoursesBannerProps> = ({ enableNewLayout }) => {
  const { classes } = useStyles();
  const { translate } = useTranslation();
  const { isFetched: isAuthLoaded, user: authenticatedUser } = useRobloxAuthentication();
  const { targetLocale, subPath } = useCurrentLocale();
  const { enableCourses, isIXPFetched, enableNewLayout: configEnableNewLayout } = useAppConfig();

  const isNewLayout = enableNewLayout ?? configEnableNewLayout;

  const userId = authenticatedUser?.id;

  const [bannerDismissed, setBannerDismissed] = useState<boolean>(true);
  const [settingLoaded, setSettingLoaded] = useState<boolean>(false);

  const isAllowedPath = ALLOWED_PATHS.has(subPath);

  useEffect(() => {
    async function fetchBannerSetting() {
      if (isAuthLoaded && isAllowedPath) {
        try {
          if (userId != null) {
            const { settingValue } = await creatorSettingsClient.getGenericCreatorSetting(
              userId,
              'GENERIC_CREATOR_SETTING_TYPE_COURSES_EXPLORE_COURSES_BANNER',
            );
            setBannerDismissed(settingValue === 'false');
          } else {
            const value = localStorage.getItem(FALLBACK_LOCAL_STORAGE_KEY);
            setBannerDismissed(value === 'false');
          }
        } catch (e) {
          setBannerDismissed(false);
          throwError(`ExploreCoursesBanner: Failed to dismiss explore courses banner: ${e}`);
        } finally {
          setSettingLoaded(true);
        }
      }
    }

    fetchBannerSetting();
  }, [isAuthLoaded, userId, isAllowedPath]);

  useEffect(() => {
    const shouldShowBanner = !bannerDismissed && settingLoaded && isAllowedPath;

    if (shouldShowBanner) {
      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsCoursesEvent.viewExploreCoursesBanner,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
        },
      });
    }
  }, [bannerDismissed, settingLoaded, targetLocale, isAllowedPath]);

  const handleDismiss = useCallback(async () => {
    setBannerDismissed(true);

    unifiedLoggerClient.logClickEvent({
      eventName: DocsCoursesEvent.clickDismissExploreCoursesBanner,
      parameters: {
        locale: targetLocale ? String(targetLocale) : '',
      },
    });

    if (isAuthLoaded) {
      try {
        if (userId != null) {
          await creatorSettingsClient.updateGenericCreatorSetting(
            userId,
            'GENERIC_CREATOR_SETTING_TYPE_COURSES_EXPLORE_COURSES_BANNER',
            'false',
          );
        } else {
          localStorage.setItem(FALLBACK_LOCAL_STORAGE_KEY, 'false');
        }
      } catch (e) {
        throwError(`ExploreCoursesBanner: Failed to fetch banner setting: ${e}`);
      }
    }
  }, [isAuthLoaded, userId, targetLocale]);

  const handleExploreCourses = () => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsCoursesEvent.clickExploreCoursesBanner,
      parameters: {
        locale: targetLocale ? String(targetLocale) : '',
      },
    });
  };

  if (!isAllowedPath || !isAuthLoaded || !userId) {
    return null;
  }

  if (!isIXPFetched || !enableCourses) {
    return null;
  }

  if (!settingLoaded) {
    return null;
  }

  if (bannerDismissed) {
    return null;
  }

  return (
    <div className={classes.bannerContainer}>
      <Banner
        classes={{
          root: isNewLayout ? classes.newLayoutRoot : classes.oldLayoutRoot,
        }}
        description={translate('Message.ExploreCoursesBanner')}
        onClose={handleDismiss}
        title={translate('Label.ExploreCoursesBanner')}
        primary={{
          label: translate('Label.ExploreCourses'),
          onClick: handleExploreCourses,
          color: 'secondary',
          variant: 'contained',
          size: 'medium',
          // Note: @neoxu (2025-09-23): /courses doesn't support locale, only the children pages /courses/xxx supports locale
          href: coursesPathSuffix,
          component: DynamicallyRouted.Button,
        }}
      />
    </div>
  );
};

export default ExploreCoursesBanner;
